import { LBtn, LFlex, LModal, LSubTitle, LTitle } from "@components";
import { CheckerIcon } from "@fassessment/components/CheckerIcon";
import { MicCheckOpenModal } from "@fassessment/components/MicCheckOpenModal";
import { MicTroubleshootModal } from "@fassessment/components/MicTroubleShootModal";
import { playAudioAsync } from "@fassessment/hooks/useAudio";
import { useCustomSpeechToText } from "@fassessment/hooks/useCustomSpeechToText";
import { faCircleLeft } from "@fortawesome/free-regular-svg-icons";
import { faMicrophone, faVolumeHigh, faWifi } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { sleepFor } from "@helpers/countHelper";
import { NavItems } from "@helpers/navigation";
import { useApi } from "@services/useApi";
import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Env } from "../../../env";

enum CheckStatus {
    Pending,
    Success,
    Failed
}

enum Step {
    Wifi,
    Audio,
    Mic
}

const MicStatuses = (
    tryNumber: number,
    toggleMicCheckOpen: (tryTwo: boolean) => void,
    toggleGetHelp: () => void
): JSX.Element => {
    return (
        <LFlex justifyCenter alignCenter className="mt-6">
            <p className="mt-5 has-text-weight-bold has-text-dark-blue">
                {tryNumber === 2
                    ? "Troubleshoot the microphone connection"
                    : "Speak loudly and clearly when responding and make sure the microphone is not blocked by anything"}
            </p>
            <LFlex row>
                {tryNumber === 2 && (
                    <LBtn classes="has-text-weight-bold mt-5" variation="outline" onClick={toggleGetHelp}>
                        Get Help
                    </LBtn>
                )}
                <LBtn
                    onClick={() => {
                        toggleMicCheckOpen(true);
                    }}
                    classes="has-text-weight-bold mt-5"
                >
                    Try Again
                </LBtn>
            </LFlex>
        </LFlex>
    );
};

const ACTION_TIMEOUT = 2500;

interface TryAgain {
    text: string;
    onClick: () => void;
}

export const AssessmentChecks = (): JSX.Element => {
    const [micCheckOpen, setMicCheckOpen] = useState(false);
    const [micTroubleshootOpen, setMicTroubleshootOpen] = useState(false);
    const [audioCheckOpen, setAudioCheckOpen] = useState(false);
    const [minMax, setMinMax] = useState<[number, number]>([-100, -100]);
    const [checkStatus, setCheckStatus] = useState<CheckStatus>(CheckStatus.Pending);
    const [tryNumber, setTryNumber] = useState(1);
    const [timeoutNav, setTimeoutNav] = useState<NodeJS.Timeout>();
    const [currentStep, setCurrentStep] = useState(Step.Wifi);
    const [tryAgainText, setTryAgainText] = useState<TryAgain | undefined>();

    const navigate = useNavigate();

    const { connectionTest } = useApi();

    const toggleMicCheckOpen = (tryTwo?: boolean): void => {
        if (micCheckOpen) {
            void stopSpeechToText();
            const newStatus = minMax[1] > -50 ? CheckStatus.Success : CheckStatus.Failed;

            setCheckStatus(newStatus);
            setMicCheckOpen(!micCheckOpen);

            if (newStatus === CheckStatus.Success) {
                const navTimeout = setTimeout(() => {
                    destroy();
                    navigate(NavItems.Assessments.Landing.path);
                }, ACTION_TIMEOUT);

                setTimeoutNav(navTimeout);
            }

            return;
        }

        if (tryTwo) {
            setTryNumber(2);
        }
        setCheckStatus(CheckStatus.Pending);
        setMicCheckOpen(!micCheckOpen);
    };

    const toggleGetHelp = (): void => {
        setMicCheckOpen(false);
        setMicTroubleshootOpen(!micTroubleshootOpen);
    };

    const toggleAudioCheck = (): void => {
        setAudioCheckOpen(!audioCheckOpen);
    };

    const tryAgainGetHelp = (): void => {
        setMicTroubleshootOpen(false);
        setCheckStatus(CheckStatus.Pending);
        setTryNumber(1);
        setMicCheckOpen(true);
    };

    const { startSpeechToText, stopSpeechToText, recordingVolume, currentDecibels, destroy } = useCustomSpeechToText({
        testOnly: true
    });

    const handleAudioCheck = async (): Promise<void> => {
        setTryAgainText(undefined);
        const newAudio = new Audio(`${Env.REACT_APP_BLOB_URL}/instructions-en-us/bells.mp3`);
        await playAudioAsync(newAudio);
        setAudioCheckOpen(true);
    };

    const handleAudioError = (): void => {
        toggleAudioCheck();
        setTryAgainText({
            text: "Connect the speakers or turn up the volume on the device",
            onClick: handleAudioCheck
        });
    };

    const handleAudioSuccess = async (): Promise<void> => {
        setAudioCheckOpen(false);
        setCurrentStep(Step.Mic);
        await sleepFor(2);
        toggleMicCheckOpen();
    };

    const handleConnectionCheck = async (): Promise<void> => {
        setTryAgainText(undefined);
        const check = await connectionTest();

        if (!check) {
            setTryAgainText({ text: "Wait a few minutes and try again", onClick: handleConnectionCheck });
        } else {
            setCurrentStep(Step.Audio);
            await sleepFor(2);
            void handleAudioCheck();
        }
    };

    const handleSteps = async (): Promise<void> => {
        await handleConnectionCheck();
    };

    useEffect(() => {
        setTimeout(() => {
            void handleSteps();
        }, 2000);
    }, []);

    useEffect(() => {
        if (micCheckOpen && isFinite(currentDecibels)) {
            setMinMax([Math.min(currentDecibels, minMax[0]), Math.max(currentDecibels, minMax[1])]);
        }
    }, [currentDecibels]);

    return (
        <>
            <LFlex alignCenter className="p-6 is-fullheight">
                <LFlex className="is-justify-content-space-between" row>
                    <div style={{ position: "absolute", left: "5%" }}>
                        <NavLink
                            to="/admin/assessments/assessmentsUpcoming"
                            onClick={() => {
                                clearTimeout(timeoutNav);
                            }}
                        >
                            <FontAwesomeIcon icon={faCircleLeft} size={"xl"} />
                            <span className="subtitle px-2 has-text-primary-blue">Back</span>
                        </NavLink>
                    </div>
                    <LFlex alignCenter justifyCenter>
                        <LTitle color="has-text-dark-blue">Device Check</LTitle>
                        <LSubTitle size="5" color="has-text-dark-blue" className="mt-3">
                            {checkStatus === CheckStatus.Success
                                ? "Device Check Completed"
                                : "Before we begin we need to ensure your equipment is working properly"}
                        </LSubTitle>
                    </LFlex>
                </LFlex>
                <div className="is-flex" style={{ height: "80vh", width: "60vw", justifyContent: "space-between" }}>
                    <LFlex alignCenter className="is-justify-content-space-evenly" row fullWidth>
                        {/* {CheckStatuses(CheckStatus.Pending, tryNumber, toggleMicCheckOpen, toggleGetHelp)} */}
                        <CheckerIcon
                            title="Internet"
                            subtitle="Checking your connection..."
                            icon={faWifi}
                            active={currentStep === Step.Wifi}
                            success={currentStep !== Step.Wifi}
                            error={!!tryAgainText?.text && currentStep === Step.Wifi}
                            errorText="Your connection cannot support this assessment at this time"
                        />
                        <div className="divider" style={{ width: "20%" }} />
                        <CheckerIcon
                            title="Speakers"
                            subtitle="Checking your speakers..."
                            icon={faVolumeHigh}
                            active={currentStep === Step.Audio}
                            error={!!tryAgainText?.text && currentStep === Step.Audio}
                            errorText="Your speakers are not projecting sound"
                            success={currentStep === Step.Mic}
                        />
                        <div className="divider" style={{ width: "20%" }} />
                        <CheckerIcon
                            title="Microphone"
                            subtitle="Checking your microphone..."
                            icon={faMicrophone}
                            active={currentStep === Step.Mic}
                            error={checkStatus === CheckStatus.Failed}
                            success={checkStatus === CheckStatus.Success}
                            errorText={
                                tryNumber === 2
                                    ? "Your microphone is not connected"
                                    : "Your microphone is having trouble picking up audio"
                            }
                        />
                    </LFlex>
                </div>
                {tryAgainText?.text && (
                    <LFlex justifyCenter alignCenter className="mt-6">
                        <p className="mt-5 has-text-weight-bold has-text-dark-blue">{tryAgainText?.text}</p>
                        <LFlex row>
                            <LBtn onClick={tryAgainText.onClick} classes="has-text-weight-bold mt-5">
                                Try Again
                            </LBtn>
                        </LFlex>
                    </LFlex>
                )}
                {checkStatus === CheckStatus.Failed && MicStatuses(tryNumber, toggleMicCheckOpen, toggleGetHelp)}
            </LFlex>
            {micCheckOpen && (
                <MicCheckOpenModal
                    startSpeechToText={startSpeechToText}
                    recordingVolume={recordingVolume ?? 0}
                    onClose={toggleMicCheckOpen}
                />
            )}

            {micTroubleshootOpen && <MicTroubleshootModal onClose={toggleGetHelp} onTryAgain={tryAgainGetHelp} />}

            {audioCheckOpen && (
                <LModal
                    title={
                        <p className="has-text-centered has-text-weight-bold">Can you hear the sound of the bells?</p>
                    }
                    hasCancelConfirmButtons
                    onClose={handleAudioError}
                    onConfirm={handleAudioSuccess}
                    width="s"
                    fullHeight={false}
                    cancelText="No"
                    confirmText="Yes"
                >
                    <div className="has-text-centered">If the sound is faint, increase the volume on your device.</div>
                </LModal>
            )}
        </>
    );
};
